import { Helmet } from 'react-helmet-async'
import { CheckCircleIcon } from '@heroicons/react/outline'

import Container from '../ui/Container'
import SectionHeading from '../ui/SectionHeading'
import { businessInfo, partners } from '../../data/site'

import './index.css'

const whyChooseUs = [
  `Registered with the Corporate Affairs Commission and accredited by the Medical Laboratory Science Council of Nigeria (MLSCN).`,
  `Accredited by the Lagos State Health Facility Monitoring and Accreditation Agency (HEFAMAA) as a secondary healthcare provider.`,
  `Two accessible diagnostic centres, in Ikorodu and Ogijo.`,
  `A wide range of diagnostic services under one roof, from routine health checks to specialised pathology.`,
  `Trusted by hospitals and clinics across our service areas as a diagnostic partner.`,
]

function SummaryContent({ onDark = false }) {
  const bodyText = onDark ? 'text-primary-100' : 'text-gray-600'
  const strongText = onDark ? 'text-white' : 'text-primary'
  const eyebrowText = onDark ? 'text-accent-200' : 'text-accent-600'

  return (
    <div id="about">
      <div className="flex flex-col w-full pb-5 items-center md:items-start text-center md:text-left">
        <span className={`text-xs font-bold uppercase tracking-widest mb-2 ${eyebrowText}`}>Who we are</span>
        <p className={`max-w-xs sm:max-w-sm md:max-w-xl lg:max-w-3xl mb-8 text-sm md:text-lg ${bodyText}`}>
          <span className={`font-semibold ${strongText}`}>Meditex Healthcare Hub Limited</span> (incorporated {businessInfo.foundedYear})
          is a quality-driven medical diagnostic centre committed to becoming a leading name in diagnostic healthcare
          across Nigeria and Africa. With a focus on delivering the best to our patients, we employ skilled hands to
          get the work done right.
        </p>
      </div>
      <div className="flex content-start text-sm mb-4">
        <div className={`flex flex-col items-start ${bodyText}`}>
          <p className={`font-bold mb-1 ${strongText}`}>Opening Hours</p>
          {businessInfo.hours.map((h) => (
            <p key={h.days}>{h.days}: {h.time}</p>
          ))}
          <p className="mt-1 text-xs max-w-sm">{businessInfo.emergencyNote}</p>
        </div>
      </div>
    </div>
  )
}

function About({ variant = 'summary', onDark = false }) {
  if (variant === 'summary') {
    return <SummaryContent onDark={onDark} />
  }

  return (
    <>
      <Helmet>
        <title>About Us | Meditex Healthcare Hub Limited</title>
        <meta
          name="description"
          content="Learn about Meditex Healthcare Hub Limited — our mission, accreditations, locations, and the hospitals and clinics that trust us as their diagnostic partner."
        />
      </Helmet>

      <div className="bg-primary py-16">
        <Container>
          <SectionHeading
            eyebrow="About Meditex"
            title="A diagnostic partner built on quality and trust"
            description={`Incorporated in ${businessInfo.foundedYear}, Meditex Healthcare Hub Limited provides a health care experience patients and partner hospitals can rely on.`}
            light
          />
        </Container>
      </div>

      <Container className="py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="bg-primary-50 rounded-xl p-8">
            <h3 className="text-xl font-bold text-primary mb-3">Our Mission</h3>
            <p className="text-gray-700">
              To provide accurate, reliable and accessible diagnostic care that families, clinicians and partner
              hospitals can depend on.
            </p>
          </div>
          <div className="bg-accent-50 rounded-xl p-8">
            <h3 className="text-xl font-bold text-primary mb-3">Our Vision</h3>
            <p className="text-gray-700">
              To grow into one of the most trusted diagnostic healthcare brands in Nigeria and across Africa.
            </p>
          </div>
        </div>
      </Container>

      <Container className="pb-16">
        <SectionHeading align="left" title="Why Choose Meditex" />
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {whyChooseUs.map((item) => (
            <li key={item} className="flex items-start gap-3 bg-white border border-gray-100 rounded-lg p-4 shadow-sm">
              <CheckCircleIcon className="h-6 w-6 text-accent shrink-0" />
              <span className="text-gray-700 text-sm">{item}</span>
            </li>
          ))}
        </ul>
      </Container>

      <div className="bg-gray-50 py-16">
        <Container>
          <SectionHeading align="left" title="Our Locations" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {businessInfo.locations.map((loc) => (
              <div key={loc.name} className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <p className="font-bold text-primary">{loc.name}</p>
                <p className="text-gray-600 text-sm mt-1">{loc.address}</p>
              </div>
            ))}
          </div>
        </Container>
      </div>

      <Container className="py-16">
        <SectionHeading align="left" title="Hospitals & Clinics That Trust Us" description="A selection of the healthcare partners we work with." />
        <div className="flex flex-wrap gap-3">
          {partners.map((partner) => (
            <span key={partner} className="bg-primary-50 text-primary-700 rounded-full px-4 py-2 text-sm font-medium">
              {partner}
            </span>
          ))}
        </div>
      </Container>
    </>
  )
}

export default About
